import { useCallback } from 'react';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { fg } from '@confluence/feature-gating';

import { type ContentMode } from './presenceTypes';
import { type PresenceParticipant } from './presence-store';
import {
	type GetTelepointerDataBySessionId,
	type TelepointerData,
} from './presence/usePresenceProvider';

type UseJumpToTelepointerArgs = {
	contentMode: ContentMode;
	getTelepointerBySessionId?: GetTelepointerDataBySessionId;
};

export const useJumpToTelepointer = ({
	contentMode,
	getTelepointerBySessionId,
}: UseJumpToTelepointerArgs) => {
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();

	const createJumpToTelepointerHandler = useCallback(
		(participant: PresenceParticipant) => {
			// TODO: https://product-fabric.atlassian.net/browse/MDS-1299
			// Idle users don't have a telepointer
			const isEligible =
				// Current user won't have the onClick handler for telepointer jump
				!participant.isCurrentUser &&
				// We don't have telepointers on view mode
				contentMode !== 'view' &&
				// Not every presence container has telepointer data (e.g. canvas)
				getTelepointerBySessionId &&
				// Only available if the FG allows us
				fg('confluence_team_presence_scroll_to_pointer');

			if (!isEligible) {
				return;
			}

			return () => {
				const telepointers = Object.keys(participant.sessions)
					.map((s) => getTelepointerBySessionId(s))
					.filter((tData): tData is TelepointerData => !!tData)
					.sort((a, b) => b?.timestamp - a?.timestamp);

				// Telepointer data of the active session based on the saved timestamp
				const cursorPos = telepointers[0]?.selection.head;

				if (cursorPos === undefined) {
					return;
				}

				fullPageEditorPresetAPI?.core?.actions?.scrollToPos(Number(cursorPos), {
					behavior: 'smooth',
					block: 'center',
				});
			};
		},
		[contentMode, fullPageEditorPresetAPI, getTelepointerBySessionId],
	);

	return { createJumpToTelepointerHandler };
};
